import React, { useEffect, useMemo, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { SingleSelectTagPicker, TagData } from "../../_base/SingleSelectTagPicker";
import { Link, makeStyles } from "@fluentui/react-components";
import { ArrowClockwise20Regular } from "@fluentui/react-icons";
import { ProjectsContextValue, useProjects } from "./ProjectsContext";
import { SmartFilingManagerFluent9 } from "@src/services/SmartFiling/SmartFilingManagerFluent9";
import { Logger } from "@src/services/Logger";
import { IProjectData } from "@src/models/shared/IProjectData";
import { InvalidateCacheService } from "@src/services/NewformaApi/InvalidateCacheService";
import { ProjectsCacheKeys } from "../../../models/StorageKeys";
import { MailboxItem, OfficeWrapper } from "@src/services/OfficeWrapper";

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
    },
    container: { display: "flex" },
    link: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        marginLeft: "auto",
        fontSize: "var(--fontSizeBase200)",
    },
});

export interface ProjectPickerFluent9Props extends LocalizeContextProps {
    smartFilingManager: SmartFilingManagerFluent9;
    invalidateCacheService: InvalidateCacheService;
    logger: Logger;
    officeWrapper: OfficeWrapper;
    autoSelectSuggested?: boolean;
    showValidationError?: boolean;
    selectedProject?: IProjectData;
    onProjectChange?: (project?: IProjectData) => void;
    onReloadProjects?: () => void;
    /** Called whenever the project picker's loading state changes. */
    onLoadingChange?: (isLoading: boolean) => void;
}

export const ProjectPickerFluent9 = withLocalize((props: ProjectPickerFluent9Props) => {
    const suggestedProjectsCount = 3;
    const myProjectsCount = 25;
    const context: ProjectsContextValue | null = useProjects();

    const styles = useStyles();

    const [suggestedProjects, setSuggestedProjects] = useState<IProjectData[]>([]);
    const [pickerResetToken, setPickerResetToken] = useState<number>(0);

    useEffect(() => {
        props.onLoadingChange?.(context?.loading ?? false);
    }, [context?.loading, props.onLoadingChange]);

    const selectedProjectDefaultHasRun = useRef(false);
    const prevMailboxItem = useRef<MailboxItem | null>(null);

    const currentMailboxItem = props.officeWrapper.currentContextItem;

    useEffect(() => {
        if (prevMailboxItem.current?.subject !== currentMailboxItem?.subject) {
            if (prevMailboxItem.current) props.onProjectChange?.(undefined);

            prevMailboxItem.current = currentMailboxItem;
            selectedProjectDefaultHasRun.current = false;

            setPickerResetToken((prev) => prev + 1);
            setSuggestedProjects([]);
        }

        const fetchSuggestions = async () => {
            if (!context || context.projects.length < 1) return;

            try {
                const suggestionResults = await props.smartFilingManager.getSuggestedProjects(context.projects, false);
                if (suggestionResults.length) {
                    setSuggestedProjects(
                        suggestionResults.slice(0, suggestedProjectsCount).map((proj) => ({
                            name: proj.name,
                            number: proj.number,
                            displayName: proj.number ? `${proj.number} - ${proj.name}` : proj.name,
                            nrn: proj.nrn,
                            nixEnabled: proj.nixEnabled,
                            isDisabled: proj.isDisable,
                            code: proj.code,
                        }))
                    );
                }
            } catch (error) {
                props.logger.error(
                    "SuggestedProjectPickerComponent. There was an error retrieving suggested projects",
                    error
                );
            }
        };

        fetchSuggestions();
    }, [context?.projects, currentMailboxItem]);

    const suggestedOptions = useMemo(() => {
        const label = props.translate("SHARED.SUGGESTED_PROJECTS.SUGGESTED_PROJECTS_TITLE").toString();
        return suggestedProjects.map((proj) => ({
            key: proj.nrn,
            label: proj.displayName,
            group: {
                key: "suggested",
                label: label,
            },
        }));
    }, [suggestedProjects]);

    const selectedPickerOption = useMemo(() => {
        if (props.selectedProject) {
            return {
                key: props.selectedProject.nrn,
                label: props.selectedProject.displayName,
            };
        }

        return undefined;
    }, [props.selectedProject]);

    const myOptions = useMemo(() => {
        const label = props.translate("SHARED.MY_PROJECTS").toString();

        if (context)
            return context.projects.map((proj) => ({
                key: proj.nrn,
                label: proj.displayName,
                group: {
                    key: "my",
                    label: label,
                },
            }));

        return [];
    }, [context?.projects]);

    const pickerOptions = useMemo(() => {
        if (context) {
            return suggestedOptions.concat(
                myOptions.filter((myOpt) => !suggestedOptions.some((sOpt) => myOpt.key === sOpt.key))
            );
        }

        return [];
    }, [myOptions, suggestedOptions]);

    useEffect(() => {
        if (props.autoSelectSuggested ?? true) {
            if (props.selectedProject) {
                selectedProjectDefaultHasRun.current = true;
            }

            if (!selectedProjectDefaultHasRun.current && suggestedProjects.length > 0) {
                props.onProjectChange?.(suggestedProjects[0]);
                selectedProjectDefaultHasRun.current = true;
            }
        }
    }, [props.selectedProject, suggestedProjects]);

    const reloadProjectsOnClick = async () => {
        if (context) {
            selectedProjectDefaultHasRun.current = false;
            await props.invalidateCacheService.invalidateCache(ProjectsCacheKeys.fileTransferCacheName);
            setPickerResetToken((prev) => prev + 1);

            context.reloadProjects();
            props.onReloadProjects?.();
        }
    };

    const onOptionSelect = (data?: TagData) =>
        props.onProjectChange?.(data ? context?.projects.find((proj) => proj.nrn === data.key) : undefined);

    return (
        <div className={styles.root}>
            <SingleSelectTagPicker
                label={props.translate("SHARED.SUGGESTED_PROJECTS.LABEL").toString()}
                name="project"
                options={pickerOptions}
                selectedOption={selectedPickerOption}
                required
                disabled={context?.loading ?? false}
                limit={myProjectsCount + suggestedProjects.length}
                placeholder={
                    context?.loading
                        ? props.translate("SEND_AND_FILE_NEW.LOADING_PROJECTS").toString()
                        : props.translate("SHARED.SUGGESTED_PROJECTS.PLACEHOLDER").toString()
                }
                noOptionsLabel={props.translate("SHARED.SUGGESTED_PROJECTS.NO_PROJECTS").toString()}
                limitExceededLabel={props.translate("SHARED.FIELD.PROJECT_LIMIT_EXCEEDED.LABEL").toString()}
                resetToken={pickerResetToken}
                onOptionSelect={onOptionSelect}
                validationMessage={
                    props.showValidationError ? props.translate("SHARED.FIELD.REQUIRED_ERROR").toString() : undefined
                }
            />
            <div className={styles.container}>
                <Link
                    data-testid="reload-projects-button"
                    onClick={reloadProjectsOnClick}
                    className={styles.link}
                    disabled={context?.loading}
                >
                    <ArrowClockwise20Regular />
                    {props.translate("SEND_AND_FILE_NEW.RELOAD_PROJECTS")}
                </Link>
            </div>
        </div>
    );
});
